import { useSyncExternalStore } from 'react';
import { clsx, type ClassValue } from 'clsx';
import { enUS, es } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const DATE_LOCALES: Record<string, Locale> = { en: enUS, es };

/** Languages Calendar/DatePicker can render; anything else falls back to en-US. */
export const DATE_LOCALE_CODES = Object.keys(DATE_LOCALES);

// One observer for the whole page, not one per picker: <html lang> is global,
// so every hook instance would otherwise watch the same attribute and derive
// the same string.
const langListeners = new Set<() => void>();
let langObserver: MutationObserver | undefined;

const subscribeToLang = (onChange: () => void) => {
  langListeners.add(onChange);
  if (!langObserver) {
    langObserver = new MutationObserver(() => langListeners.forEach(l => l()));
    langObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['lang'],
    });
  }
  return () => {
    langListeners.delete(onChange);
    if (langListeners.size === 0) {
      langObserver?.disconnect();
      langObserver = undefined;
    }
  };
};

const readLang = () => document.documentElement.lang || 'en';

/**
 * date-fns locale for the app's current language, read from <html lang>.
 *
 * This package stays free of the app's i18n stack on purpose, so the language
 * travels via the DOM attribute the platform already defines for it (which
 * screen readers need set correctly anyway). Switching language calls
 * i18next.changeLanguage() without a reload, so this subscribes rather than
 * reading once — a Calendar whose parent does not itself call t() would
 * otherwise keep rendering the old language until something else re-rendered it.
 */
export function useDateLocale(): Locale {
  const lang = useSyncExternalStore(subscribeToLang, readLang, () => 'en');
  return DATE_LOCALES[lang.split('-')[0]] ?? enUS;
}

/** Default heltar-branded Switch className */
export const SWITCH_HELTAR_CLASS =
  '!switch-heltar !data-[state=checked]:bg-[var(--heltar-brand-primary)]' as const;

/** Switch className with a custom checked color */
export const switchHeltarColorClass = (color: string): string =>
  `!switch-heltar !data-[state=checked]:bg-${color}`;
